import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, categoryText, enc, photoUrl, type Product } from '../api';
import { ErrorBox, Empty, Loading, Money, Pager, Val, fmtDate } from '../components/ui';
import { useDebounced } from '../hooks';

type Row = Product & { webPhotoId: string | null; lotPhotoId: string | null };
type SortKey = 'itemNumber' | 'description' | 'listingTitle' | 'retail' | 'sell' | 'category' | 'source' | 'added';

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'itemNumber', label: 'Item #' },
  { key: 'description', label: 'Description (imported)' },
  { key: 'listingTitle', label: 'Listing title' },
  { key: 'retail', label: 'Retail' },
  { key: 'sell', label: 'Sell' },
  { key: 'category', label: 'Category' },
  { key: 'source', label: 'Source' },
  { key: 'added', label: 'Added' },
];

/** Costco catalogue photo if there is one, otherwise a photo of one of our lots of this product. */
function ProductThumb({ p }: { p: Row }) {
  if (p.webPhotoId) return <img className="thumb" src={`/api/web-images/${p.webPhotoId}/thumb`} width={48} height={48} alt="" loading="lazy" title="Costco catalogue photo" />;
  if (p.lotPhotoId) return <img className="thumb" src={photoUrl(p.lotPhotoId)} width={48} height={48} alt="" loading="lazy" title="Photo of one of our lots" />;
  return (
    <span className="thumb thumb-none" style={{ width: 48, height: 48 }}>
      no photo
    </span>
  );
}

export function ProductsPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Number(params.get('page') ?? 1) || 1;
  const sort = (params.get('sort') as SortKey | null) ?? null;
  const dir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const category = params.get('category');
  const [q, setQ] = useState(params.get('q') ?? '');
  const dq = useDebounced(q.trim());

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  useEffect(() => {
    if ((params.get('q') ?? '') !== dq) update({ q: dq || null, page: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq]);

  const sortBy = (key: SortKey) => update({ sort: key, dir: sort === key && dir === 'asc' ? 'desc' : 'asc', page: null });

  const query = new URLSearchParams({ page: String(page) });
  if (dq) query.set('q', dq);
  if (category) query.set('category', category);
  if (sort) (query.set('sort', sort), query.set('dir', dir));
  const res = useQuery({
    queryKey: ['products', query.toString()],
    queryFn: () => api<{ products: Row[]; total: number; page: number; pageSize: number }>(`/products?${query}`),
    placeholderData: keepPreviousData,
  });

  return (
    <div>
      <div className="row between wrap">
        <h1>Products</h1>
        <div className="row gap">
          <Link className="btn" to="/imports/new">
            Import products
          </Link>
          <Link className="btn btn-primary" to="/products/new">
            New product
          </Link>
        </div>
      </div>
      <div className="filters">
        <input type="search" placeholder="Item number, title or linked barcode" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
        <select value={category ?? ''} onChange={(e) => update({ category: e.target.value || null, page: null })} aria-label="Category">
          <option value="">All categories</option>
          <option value="hard_goods">Hard Goods</option>
          <option value="clothing">Clothing</option>
          <option value="none">No category yet</option>
        </select>
        {sort && (
          <button className="btn btn-ghost" onClick={() => update({ sort: null, dir: null, page: null })}>
            Clear sort
          </button>
        )}
      </div>
      {res.isLoading && <Loading />}
      <ErrorBox error={res.error} onRetry={() => res.refetch()} />
      {res.data && res.data.products.length === 0 && (
        <Empty>{dq ? `No products match "${dq}".` : 'No products yet. Import a manifest CSV or create one manually.'}</Empty>
      )}
      {res.data && res.data.products.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Photo</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} aria-sort={sort === c.key ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <button type="button" className={`sort-btn ${sort === c.key ? 'active' : ''}`} onClick={() => sortBy(c.key)}>
                      {c.label}
                      <span className="sort-arrow" aria-hidden>
                        {sort === c.key ? (dir === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {res.data.products.map((p) => (
                <tr key={p.id} className="clickable" onClick={() => nav(`/products/${p.id}`)}>
                  <td>
                    <ProductThumb p={p} />
                  </td>
                  <td className="mono">
                    <Link to={`/products/${enc(p.id)}`}>{p.itemNumber}</Link>
                    {p.hasWebData && (
                      <span title="Costco web data available" aria-label="Costco web data available">
                        {' '}
                        🌐
                      </span>
                    )}
                  </td>
                  <td>
                    <Val v={p.manifestTitle} missing="—" />
                  </td>
                  <td>
                    <Val v={p.listingTitle} missing="—" />
                  </td>
                  <td>
                    <Money amount={p.manifestPrice} currency={p.manifestCurrency} />
                  </td>
                  <td>
                    <Money amount={p.manifestSellPrice ?? null} currency={p.manifestCurrency} />
                  </td>
                  <td className="small">{categoryText(p.mainCategory, p.realSubcategory) ?? ''}</td>
                  <td className="small">{p.source === 'csv_import' ? 'Import' : p.source === 'web' ? 'Costco website' : 'Manual'}</td>
                  <td className="small">{fmtDate(p.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {res.data && <Pager page={res.data.page} pageSize={res.data.pageSize} total={res.data.total} onPage={(n) => update({ page: n > 1 ? String(n) : null })} />}
    </div>
  );
}
